"use client";

import { IconMagnifyingGlass } from "central-icons-outlined/IconMagnifyingGlass";
import { AnimatePresence, m, useReducedMotion } from "motion/react";
import { useState } from "react";
import Comparison from "@/components/preview/comparison";
import { useSpeech } from "@/hooks/use-speech";
import { cn } from "@/lib/utils";

const crossfadeTransition = {
  bounce: 0,
  duration: 0.4,
  type: "spring" as const,
};

function SpokenText({
  charIndex,
  text,
}: {
  charIndex: number | null;
  text: string;
}) {
  let offset = 0;
  const words = text.split(" ").map((word) => {
    const start = offset;
    // oxlint-disable-next-line react/immutability -- Array.map runs synchronously with a render-local counter.
    offset += word.length + 1;
    return { start, word };
  });
  const spokenIndex =
    charIndex === null
      ? null
      : words.findLastIndex(({ start }) => charIndex >= start);

  return (
    <span
      aria-hidden="true"
      className="flex select-none flex-wrap items-center justify-center gap-x-1 whitespace-nowrap text-center font-medium text-gray-1200 text-sm will-change-transform"
    >
      {words.map(({ start, word }, index) => (
        <span
          className={cn(
            "transition-opacity duration-150 ease-out",
            spokenIndex !== null && index !== spokenIndex && "opacity-40"
          )}
          key={start}
        >
          {word}
        </span>
      ))}
    </span>
  );
}

function SpokenPill({
  charIndex,
  text,
}: {
  charIndex: number | null;
  text: string;
}) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <span className="relative flex w-full justify-center">
      <AnimatePresence initial={false} mode="popLayout">
        <m.span
          animate={{ filter: "blur(0px)", opacity: 1 }}
          className="flex w-full justify-center"
          exit={{ filter: "blur(5px)", opacity: 0 }}
          initial={{ filter: "blur(5px)", opacity: 0 }}
          key={text}
          transition={
            shouldReduceMotion ? { duration: 0 } : crossfadeTransition
          }
        >
          <SpokenText charIndex={charIndex} text={text} />
        </m.span>
      </AnimatePresence>
    </span>
  );
}

export default function AccessibilityIconLabelsDemo() {
  const [speaking, setSpeaking] = useState<{
    charIndex: number;
    id: string;
  } | null>(null);
  const { speak } = useSpeech();

  const announce = (id: string, announcement: string) => {
    setSpeaking(null);
    speak(announcement, {
      onBoundary: (charIndex) => setSpeaking({ charIndex, id }),
      onEnd: () => setSpeaking(null),
    });
  };

  const panel = (id: string, announcement: string, bad: boolean) => (
    <>
      <button
        aria-label={`Play the announcement for the button with ${
          bad ? "no aria-label" : "a descriptive aria-label"
        }`}
        className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-preview-bg text-gray-1000 shadow-custom transition-transform duration-200 ease-out will-change-transform hover-hover:hover:text-gray-1200 active:scale-[0.97]"
        onClick={() => announce(id, announcement)}
        type="button"
      >
        <IconMagnifyingGlass className="size-4.5" />
      </button>

      <SpokenPill
        charIndex={speaking?.id === id ? speaking.charIndex : null}
        text={announcement}
      />
    </>
  );

  return (
    <Comparison
      bad={panel("unlabeled", "button", true)}
      good={panel("labeled", "Search, button", false)}
      panelClassName="h-44 gap-6 px-6"
      stackBelow="md"
    />
  );
}
